/**
 * Formats a meal object into the correct API payload structure.
 *
 * IMPORTANT:
 * - `mode === 'update'` → payload is sent to PATCH /meals/{id}
 * - `mode === 'create'` → payload is sent to POST /meals
 *   - If `meal_id` is provided, POST acts as an UPSERT and fully replaces items
 *   - If `meal_id` is omitted, a new meal is created
 *
 * @param {Object} meal
 * @param {number} mealType
 * @param {'create' | 'update'} mode
 * @param {number | null} mealId Existing meal id for upsert
 * @returns {Object|null} Formatted payload ready for API call
 */
export const formatMealPayload = (meal, mealType, mode, mealId) => {
	if (!meal || !Array.isArray(meal.items)) return null;

	if (typeof mealType !== "number" || !Number.isFinite(mealType)) {
		console.error("Invalid mealType:", mealType);
		return null;
	}

	const formattedItems = meal.items.map((item) => {
		const rawAmount = parseFloat(String(item.amount).replace(",", ".")) || 0;

		const foodItemId =
			item.food_item_id === null
				? 0
				: item.food_item_id && Number.isFinite(Number(item.food_item_id))
					? Number(item.food_item_id)
					: 0;

		const unitId = typeof item.unit === "object" ? Number(item.unit?.id) || 0 : Number(item.unit) || 0;

		const isCustom = foodItemId === 0;

		const base = {
			amount: parseFloat(rawAmount.toFixed(2)),
			unit: unitId,
			food_item_id: foodItemId,
		};

		// CUSTOM ITEM
		if (isCustom) {
			return {
				...base,
				...(mode === "update" && { id: item.id }),
				name: item.name?.trim() || "Egen måltid",
				kcal: Number(item.kcal) || 0,
				protein: Number(item.protein) || 0,
				fat: Number(item.fat) || 0,
				carbs: Number(item.carbs) || 0,
			};
		}

		// DB ITEM
		if (mode === "update") {
			return {
				...base,
				id: item.id,
			};
		}

		return base;
	});

	const payload = {
		meal_type: mealType,
		items: formattedItems,
		name: meal.name || "",
		...(mealId != null && { meal_id: mealId }),
	};

	if (mode === "create") {
		payload.time = meal.time || "";

		if (!mealId && meal.isFavorite) payload.favorite_id = meal.id;
		if (!mealId && meal.isRecipe) payload.recipe_id = meal.id;
	}

	if (mode === "update" && meal.time && meal.time !== "00:00") {
		payload.time = meal.time;
	}

	return payload;
};

/**
 * Builds the payload for date-based meal or exercise actions.
 * Shape depends on action type and whether the entry is exercise.
 */
export const buildDatePickerPayload = ({
	isExercise,
	action,
	currentDay,
	targetDate,
	targetMealType,
	selectedEntryId,
	favoriteName,
}) => {
	if (isExercise) {
		return {
			from_date: currentDay,
			to_date: targetDate,
		};
	}

	if (action === "add") {
		return {
			date: targetDate,
			mealType: targetMealType,
			name: favoriteName || "",
		};
	}

	return {
		id: selectedEntryId,
		meal_type: targetMealType,
		from_date: currentDay,
		to_date: targetDate,
	};
};

/**
 * Builds the API payload for adding or updating an exercise entry.
 * Exercise types: standard (1), steps (2) and custom (3).
 */
export const buildExercisePayload = ({ isCustom, form, customData }) => {
	if (isCustom) {
		return {
			exercise_type: 3,
			exercise_duration: Number(customData.duration),
			exercise_time: customData.time,
			exercise_comment: customData.comment?.trim(),
			exercise_name: customData.name.trim(),
			exercise_kcal: Number(customData.kcal),
		};
	}

	const type = Number(form.exercise_type);

	const base = {
		exercise_type: type,
		exercise_time: form.exercise_time,
		exercise_comment: form.exercise_comment?.trim(),
	};

	if (type === 1) {
		return {
			...base,
			exercise_duration: Number(form.exercise_duration),
			exercise_id: Number(form.exercise_id),
		};
	}

	if (type === 2) {
		return {
			...base,
			exercise_steps: Number(form.exercise_steps),
		};
	}

	return base;
};

/**
 * Builds the payload for adding or updating a favorite exercise item.
 * Normalizes time, duration, and custom vs predefined exercises.
 */
export const buildFavoriteItemPayload = ({ isCustom, form, customData, normalizeTime }) => {
	const base = {
		duration: Number(isCustom ? customData.duration : form.exercise_duration),
		time: normalizeTime(isCustom ? customData.time : form.exercise_time),
		comment: (isCustom ? customData.comment : form.exercise_comment)?.trim() || "",
	};

	return isCustom
		? {
				...base,
				name: customData.name.trim(),
				kcal: Number(customData.kcal),
			}
		: {
				...base,
				exercise_id: Number(form.exercise_id),
			};
};

const parseOptionalNumber = (value) => {
	if (value === "") return null;
	const parsed = parseFloat(value);
	return Number.isNaN(parsed) ? null : parsed;
};

// OWN MEAL PAYLOAD BUILDER

export const buildOwnMealPayload = (form, mealType) => {
	const trimmedName = form.name.trim();

	const kcal = parseFloat(form.kcal);
	if (!trimmedName || Number.isNaN(kcal) || mealType === null || mealType === undefined) return null;

	const protein = parseOptionalNumber(form.protein);
	const carbs = parseOptionalNumber(form.carbs);
	const fat = parseOptionalNumber(form.fat);

	const item = {
		food_item_id: 0,
		amount: 1,
		unit: -1,
		name: trimmedName,
		kcal,
	};

	if (protein !== null) item.protein = protein;
	if (carbs !== null) item.carbs = carbs;
	if (fat !== null) item.fat = fat;

	return {
		payload: {
			meal_type: mealType,
			name: trimmedName,
			items: [item],
		},
		parsed: { trimmedName, kcal, protein, carbs, fat },
	};
};
